/**
 * Nguồn sự thật về chế độ giao diện cho toàn admin.
 *
 * Đòn bẩy lớn nhất của file này là `theme.darkAlgorithm` của antd: 129/201 file
 * admin dùng antd, nên Table, Modal, Select, DatePicker, Drawer, Dropdown, Tabs,
 * Tag, Pagination... tối theo mà KHÔNG phải sửa từng file.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ConfigProvider, theme as antdTheme } from "antd";

import {
  ANTD_BG,
  ANTD_BG_ELEVATED,
  ANTD_BG_LAYOUT,
  BRAND_PRIMARY,
  applyTheme,
  getMode,
  resolveDark,
  setMode as persistMode,
  type ThemeMode,
} from "./theme-storage";

type ThemeContextValue = {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  mode: "system",
  isDark: false,
  setMode: () => {},
});

export function useThemeMode(): ThemeContextValue {
  return useContext(ThemeContext);
}

export default function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(getMode);
  const [isDark, setIsDark] = useState<boolean>(() => resolveDark(getMode()));

  useEffect(() => {
    setIsDark(applyTheme(mode));
  }, [mode]);

  // Theo dõi cài đặt hệ điều hành — CHỈ khi để mức "Theo hệ thống".
  // Chọn cứng Sáng/Tối thì không đăng ký listener, tránh bị máy ghi đè.
  useEffect(() => {
    if (mode !== "system") return undefined;
    if (typeof window.matchMedia !== "function") return undefined;

    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => setIsDark(applyTheme("system"));

    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    persistMode(next);
    setModeState(next);
  }, []);

  const value = useMemo(
    () => ({ mode, isDark, setMode }),
    [mode, isDark, setMode],
  );

  // Ép các token nền của antd về đúng bảng màu của ta. Mặc định darkAlgorithm
  // dùng #141414 (xám trung tính) — để nguyên thì vùng antd và vùng Tailwind
  // hiện hai sắc xám lệch nhau, nhìn như lỗi render.
  const antdConfig = useMemo(
    () => ({
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: BRAND_PRIMARY,
        colorBgContainer: isDark ? ANTD_BG.dark : ANTD_BG.light,
        colorBgLayout: isDark ? ANTD_BG_LAYOUT.dark : ANTD_BG_LAYOUT.light,
        colorBgElevated: isDark ? ANTD_BG_ELEVATED.dark : ANTD_BG_ELEVATED.light,
      },
    }),
    [isDark],
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
}
